import { Controller } from "@hotwired/stimulus"

// ⋯ menu that portals its dropdown to document.body to escape overflow:hidden cards.
export default class extends Controller {
  static targets = ["items"]

  disconnect() {
    this._removePortal()
    this._closeReasonModal()
  }

  toggle(event) {
    event.stopPropagation()
    if (this._portal) {
      this._removePortal()
    } else {
      this._openPortal(event.currentTarget)
    }
  }

  _openPortal(button) {
    const rect = button.getBoundingClientRect()

    const menu = document.createElement("div")
    Object.assign(menu.style, {
      position:     "fixed",
      top:          `${rect.bottom + 4}px`,
      right:        `${window.innerWidth - rect.right}px`,
      background:   "#fff",
      border:       "1px solid #f3f4f6",
      borderRadius: "12px",
      boxShadow:    "0 8px 24px rgba(0,0,0,0.14)",
      // Explicit width, not shrink-to-fit: with only minWidth set, WKWebView
      // resolved the width to full-screen on some pages (e.g. the match page),
      // so the dropdown spanned edge-to-edge instead of sitting top-right.
      width:        "210px",
      maxWidth:     "calc(100vw - 24px)",
      zIndex:       "99997",
      overflow:     "hidden",
    })

    // <template data-card-menu-target="items"> holds the action definitions.
    // Must query .content (DocumentFragment) to reach the buttons inside.
    if (this.hasItemsTarget) {
      this.itemsTarget.content.querySelectorAll("button[data-action-label]").forEach(item => {
        const btn = document.createElement("button")
        Object.assign(btn.style, {
          width:      "100%",
          padding:    "13px 16px",
          background: "none",
          border:     "none",
          textAlign:  "left",
          fontFamily: "-apple-system, BlinkMacSystemFont, sans-serif",
          fontSize:   "14px",
          fontWeight: "500",
          color:      item.dataset.actionColor || "#dc2626",
          cursor:     "pointer",
        })
        btn.textContent = item.dataset.actionLabel
        btn.addEventListener("click", (e) => {
          e.stopPropagation()
          this._removePortal()
          if (item.dataset.actionNavigate === "true") {
            window.location.href = item.dataset.actionUrl
          } else if (item.dataset.actionReason === "true") {
            this._showReasonModal(
              item.dataset.actionUrl,
              item.dataset.actionMethod || "POST",
              item.dataset.actionReasonTitle,
              item.dataset.actionReasonBody,
              item.dataset.actionSuccessUrl,
              item.dataset.actionReasonSubmitLabel
            )
          } else {
            this._runAction(
              item.dataset.actionUrl,
              item.dataset.actionMethod || "POST",
              item.dataset.actionReload === "true",
              item.dataset.actionSuccessUrl
            )
          }
        })
        menu.appendChild(btn)
      })
    }

    document.body.appendChild(menu)
    this._portal = menu

    this._outside = (e) => {
      if (!menu.contains(e.target) && !this.element.contains(e.target)) {
        this._removePortal()
      }
    }
    requestAnimationFrame(() => document.addEventListener("click", this._outside))
  }

  _removePortal() {
    this._portal?.remove()
    this._portal = null
    document.removeEventListener("click", this._outside)
  }

  _showReasonModal(
    url,
    method,
    title = "Report content",
    body = "Please provide details about why you're reporting this content.",
    successUrl = "/discoveries",
    submitLabel = "Submit report"
  ) {
    this._closeReasonModal()

    const overlay = document.createElement("div")
    overlay.className = "reason-sheet-overlay"

    overlay.innerHTML = `
      <section class="reason-sheet" role="dialog" aria-modal="true" aria-labelledby="reason-sheet-title" aria-describedby="reason-sheet-description">
        <header class="reason-sheet__header">
          <div class="reason-sheet__icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="9"></circle>
              <path d="M12 8v5"></path>
              <path d="M12 16h.01"></path>
            </svg>
          </div>
          <div class="reason-sheet__heading">
            <h2 id="reason-sheet-title"></h2>
            <p id="reason-sheet-description"></p>
          </div>
          <button type="button" class="reason-sheet__close" aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round">
              <path d="M6 6l12 12"></path>
              <path d="M18 6L6 18"></path>
            </svg>
          </button>
        </header>
        <label class="reason-sheet__label" for="reason-sheet-details">Details</label>
        <textarea id="reason-sheet-details" class="reason-sheet__textarea" rows="4" placeholder="Describe what happened"></textarea>
        <p class="reason-sheet__error" role="status" hidden>Something went wrong. Please try again.</p>
        <div class="reason-sheet__actions">
          <button type="button" class="reason-sheet__cancel">Cancel</button>
          <button type="button" class="reason-sheet__submit"></button>
        </div>
      </section>
    `

    const dialog = overlay.querySelector(".reason-sheet")
    const textarea = overlay.querySelector(".reason-sheet__textarea")
    const submitBtn = overlay.querySelector(".reason-sheet__submit")
    const errorMessage = overlay.querySelector(".reason-sheet__error")

    overlay.querySelector("#reason-sheet-title").textContent = title
    overlay.querySelector("#reason-sheet-description").textContent = body
    submitBtn.textContent = submitLabel

    this._reasonOverlay = overlay
    this._previousBodyOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    document.body.appendChild(overlay)

    this._reasonKeydown = (event) => {
      if (event.key === "Escape") this._closeReasonModal()
    }
    document.addEventListener("keydown", this._reasonKeydown)

    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) this._closeReasonModal()
    })
    dialog.addEventListener("click", (event) => event.stopPropagation())
    overlay.querySelector(".reason-sheet__close").addEventListener("click", () => this._closeReasonModal())
    overlay.querySelector(".reason-sheet__cancel").addEventListener("click", () => this._closeReasonModal())

    requestAnimationFrame(() => textarea.focus())

    submitBtn.addEventListener("click", async () => {
      const reason = textarea.value.trim()
      errorMessage.hidden = true
      submitBtn.disabled = true
      submitBtn.textContent = "Submitting..."

      try {
        const resp = await fetch(url, {
          method,
          headers: {
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
            "Content-Type": "application/json",
            "Accept":       "application/json",
          },
          body: JSON.stringify({ reason }),
        })
        if (resp.ok) {
          this._closeReasonModal()
          Turbo.cache.clear()
          Turbo.visit(successUrl, { action: "replace" })
        } else {
          submitBtn.disabled = false
          submitBtn.textContent = submitLabel
          errorMessage.hidden = false
          console.error("[card-menu] report failed:", resp.status)
        }
      } catch (err) {
        submitBtn.disabled = false
        submitBtn.textContent = submitLabel
        errorMessage.hidden = false
        console.error("[card-menu] report error:", err)
      }
    })
  }

  _closeReasonModal() {
    if (!this._reasonOverlay) return

    this._reasonOverlay.remove()
    this._reasonOverlay = null
    document.removeEventListener("keydown", this._reasonKeydown)
    this._reasonKeydown = null
    document.body.style.overflow = this._previousBodyOverflow || ""
    this._previousBodyOverflow = null
  }

  async _runAction(url, method, reload = false, successUrl = "/discoveries") {
    let resp
    try {
      resp = await fetch(url, {
        method,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          "Accept":       "application/json",
        }
      })
    } catch (err) {
      console.error("[card-menu] fetch failed:", err)
      return
    }

    if (!resp.ok) {
      console.error("[card-menu] action failed:", resp.status, url)
      return
    }

    if (reload) {
      // Keep the card but refresh the feed (e.g. after leaving an event)
      window.location.href = successUrl
    } else {
      // Animate the card out and remove it from the DOM. The controller lives on
      // (or inside) the .disc-card, so close on the card itself — NOT its parent,
      // which is the whole #feed-list container.
      const card = this.element.closest("turbo-frame") || this.element.closest(".disc-card")
      if (card) {
        Object.assign(card.style, {
          transition: "opacity 0.25s ease, transform 0.25s ease",
          opacity:    "0",
          transform:  "scale(0.97)",
        })
        setTimeout(() => {
          card.remove()
          // If that was the last card in the feed, reload so the server can
          // render the empty state (which lives in a branch that only shows
          // when the feed is empty — a bare #feed-list would otherwise be blank).
          const feed = document.getElementById("feed-list")
          if (feed && !feed.querySelector(".disc-card")) {
            window.location.href = "/discoveries"
          }
        }, 260)
      }
    }
  }
}
